// Login screen component

import { getGoogleAuthUrl, getGitHubAuthUrl } from './auth';

export interface LoginOptions {
  container: HTMLElement;
  error?: string;
  pending?: boolean;
  onRetry?: () => void;
  onLogout?: () => void;
}

export function renderLoginScreen(options: LoginOptions): void {
  const { container, error, pending, onRetry, onLogout } = options;

  // Clear container
  container.innerHTML = '';

  // Create login container
  const loginDiv = document.createElement('div');
  loginDiv.className = 'login-screen';

  // Logo/Title
  const title = document.createElement('h1');
  title.textContent = 'Lera';
  title.className = 'login-title';
  loginDiv.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.textContent = 'LuaJIT-Scripted MUD Client';
  subtitle.className = 'login-subtitle';
  loginDiv.appendChild(subtitle);

  // Show pending message
  if (pending) {
    const pendingDiv = document.createElement('div');
    pendingDiv.className = 'login-pending';
    pendingDiv.innerHTML = `
      <h2>Account Pending</h2>
      <p>Your account is awaiting admin approval.</p>
      <p>You'll receive access once approved.</p>
    `;
    loginDiv.appendChild(pendingDiv);

    // Button container for pending state
    const btnContainer = document.createElement('div');
    btnContainer.className = 'login-pending-buttons';

    if (onRetry) {
      const retryBtn = document.createElement('button');
      retryBtn.className = 'login-button retry';
      retryBtn.textContent = 'Check Again';
      retryBtn.onclick = onRetry;
      btnContainer.appendChild(retryBtn);
    }

    if (onLogout) {
      const logoutBtn = document.createElement('button');
      logoutBtn.className = 'login-button logout';
      logoutBtn.textContent = 'Log Out';
      logoutBtn.onclick = onLogout;
      btnContainer.appendChild(logoutBtn);
    }

    if (btnContainer.children.length > 0) {
      loginDiv.appendChild(btnContainer);
    }

    container.appendChild(loginDiv);
    return;
  }

  // Show error message
  if (error) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'login-error';
    errorDiv.textContent = error === 'suspended'
      ? 'Your account has been suspended.'
      : `Error: ${error}`;
    loginDiv.appendChild(errorDiv);
  }

  // Login buttons
  const buttonsDiv = document.createElement('div');
  buttonsDiv.className = 'login-buttons';

  // Google button
  const googleBtn = document.createElement('a');
  googleBtn.href = getGoogleAuthUrl();
  googleBtn.className = 'login-button google';
  googleBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    Sign in with Google
  `;
  buttonsDiv.appendChild(googleBtn);

  // GitHub button
  const githubBtn = document.createElement('a');
  githubBtn.href = getGitHubAuthUrl();
  githubBtn.className = 'login-button github';
  githubBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
    Sign in with GitHub
  `;
  buttonsDiv.appendChild(githubBtn);

  loginDiv.appendChild(buttonsDiv);

  // Footer
  const footer = document.createElement('div');
  footer.className = 'login-footer';
  footer.innerHTML = '<p>New users require admin approval</p>';
  loginDiv.appendChild(footer);

  container.appendChild(loginDiv);
}

// CSS for login screen (injected once)
const loginStyles = `
.login-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #1a1a2e;
  color: #e0e0e0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 20px;
}

.login-title {
  font-size: 3rem;
  margin: 0 0 0.5rem;
  color: #00d4aa;
}

.login-subtitle {
  margin: 0 0 2rem;
  color: #888;
}

.login-error {
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid #ff4444;
  color: #ff6666;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.login-pending {
  text-align: center;
  margin-bottom: 1.5rem;
}

.login-pending h2 {
  color: #ffaa00;
  margin-bottom: 0.5rem;
}

.login-pending p {
  color: #888;
  margin: 0.25rem 0;
}

.login-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 300px;
}

.login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: transform 0.1s, box-shadow 0.1s;
  cursor: pointer;
  border: none;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.login-button.google {
  background: #fff;
  color: #333;
}

.login-button.github {
  background: #24292e;
  color: #fff;
}

.login-button.retry {
  background: #00d4aa;
  color: #1a1a2e;
}

.login-button.logout {
  background: #444;
  color: #e0e0e0;
}

.login-pending-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.login-footer {
  margin-top: 2rem;
  color: #666;
  font-size: 0.875rem;
}
`;

// Inject styles once
let stylesInjected = false;
export function injectLoginStyles(): void {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.textContent = loginStyles;
  document.head.appendChild(style);
  stylesInjected = true;
}
